import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

/**
 * App-level navigation + intent bus (pattern from the Vaquill analysis,
 * docs/ui-review-cards-and-tabs-plan.md Part B).
 *
 * Tabs are the feature-registry ids. `navigate(tab, intent)` switches the tab
 * AND hands the target surface a typed, pre-filled next step — so results in
 * one place become one-tap actions in another. The target reads `intent` in a
 * useEffect, acts, and calls `clearIntent()`, so an intent fires exactly once.
 */

export type AppTab = 'ai-features' | 'tools' | 'settings';

export type AppIntent =
  /** Jump to Settings, optionally scrolling to / highlighting a section. */
  | { kind: 'openSettings'; section?: 'key' | 'models' | 'playbooks' }
  /** Open the AI edit dialog (Optimize), optionally preselecting playbook and scope. */
  | { kind: 'startAiEdit'; scope?: 'selection' | 'document'; playbookId?: string }
  /** Switch AI Buddy to Review, preselecting a persona playbook. */
  | { kind: 'startReview'; scope?: 'selection' | 'document'; playbookId?: string };

export interface AppNav {
  tab: AppTab;
  intent: AppIntent | null;
  navigate: (tab: AppTab, intent?: AppIntent) => void;
  setTab: (tab: AppTab) => void;
  clearIntent: () => void;
}

const NavContext = createContext<AppNav | null>(null);

export function useAppNav(): AppNav {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error('useAppNav must be used within <AppNavProvider>');
  return ctx;
}

/** Tools is the default landing tab: annotating/placeholders is the everyday
 *  work, so the pane opens where the hands already are. */
export const DEFAULT_TAB: AppTab = 'tools';

export function AppNavProvider({ children }: { children: ReactNode }): JSX.Element {
  const [tab, setTab] = useState<AppTab>(DEFAULT_TAB);
  const [intent, setIntent] = useState<AppIntent | null>(null);

  const navigate = useCallback((nextTab: AppTab, nextIntent?: AppIntent) => {
    setTab(nextTab);
    setIntent(nextIntent ?? null);
  }, []);

  const clearIntent = useCallback(() => setIntent(null), []);

  const value = useMemo<AppNav>(
    () => ({ tab, intent, navigate, setTab, clearIntent }),
    [tab, intent, navigate, clearIntent],
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}
